import { queryOptions, useMutation } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import {
  AspirationTodayResponse,
  AvgsResponse,
  CreateAspPoolRequest,
  EmbryologistDataEntryRequest,
  FertilizationDataResponse,
  OpuEntriesResponse,
  OpuStatsResponse,
  PoolResponse,
  QcDataResponse,
  QcDataUpdateRequest,
  RemoveAspPoolRequest,
} from './types';

export type {
  AspirationTodayResponse,
  AvgsResponse,
  CreateAspPoolRequest,
  EmbryologistDataEntryRequest,
  FertilizationDataResponse,
  OpuEntriesResponse,
  OpuStatsResponse,
  PoolResponse,
  QcDataResponse,
  QcDataUpdateRequest,
  RemoveAspPoolRequest,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(path: string, params?: URLSearchParams): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/opu-search/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/opu-search/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
  params?: URLSearchParams,
): Promise<T> {
  const baseUrl = `${getBaseUrl()}/api/v1/opu-search/${path}`;
  const url = params ? `${baseUrl}?${params.toString()}` : baseUrl;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchCompletedAspirations(params: {
  date: string;
  siteId: string;
}): Promise<AspirationTodayResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  return fetchGet<AspirationTodayResponse[]>(
    'aspirations/completed',
    searchParams,
  );
}

async function fetchCompletedAspirationsFbd(params: {
  date: string;
  siteId: string;
  location: string;
}): Promise<AspirationTodayResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  searchParams.set('location', params.location);
  return fetchGet<AspirationTodayResponse[]>(
    'aspirations/completed/fbd',
    searchParams,
  );
}

async function fetchFertData(params: {
  donorId: string;
  ranch?: string | null;
}): Promise<FertilizationDataResponse[]> {
  const searchParams = new URLSearchParams();
  if (params.ranch) searchParams.set('ranch', params.ranch);
  return fetchGet<FertilizationDataResponse[]>(
    `fert-data/${params.donorId}`,
    searchParams.toString() ? searchParams : undefined,
  );
}

async function fetchStats(params: {
  aspirationDate: string;
  siteId: string;
  location?: string | null;
}): Promise<OpuStatsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<OpuStatsResponse>('stats', searchParams);
}

async function fetchStatsPse(params: {
  aspirationDate: string;
  siteId: string;
  location?: string | null;
}): Promise<OpuStatsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<OpuStatsResponse>('stats/pse', searchParams);
}

async function fetchEntries(params: {
  aspirationDate: string;
  siteId: string;
  techId: string;
}): Promise<OpuEntriesResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  searchParams.set('techId', params.techId);
  return fetchGet<OpuEntriesResponse[]>('entries', searchParams);
}

async function fetchEntriesPse(params: {
  aspirationDate: string;
  siteId: string;
  pseId: string;
}): Promise<OpuEntriesResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  searchParams.set('pseId', params.pseId);
  return fetchGet<OpuEntriesResponse[]>('entries/pse', searchParams);
}

async function fetchTechAverages(params: {
  aspirationDate: string;
  siteId: string;
  techId: string;
}): Promise<AvgsResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  searchParams.set('techId', params.techId);
  return fetchGet<AvgsResponse[]>('tech-averages', searchParams);
}

async function fetchAverages(params: {
  aspirationDate: string;
  siteId: string;
}): Promise<AvgsResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  return fetchGet<AvgsResponse[]>('averages', searchParams);
}

async function fetchPool(params: {
  aspirationDate: string;
  siteId: string;
}): Promise<PoolResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  return fetchGet<PoolResponse[]>('pool', searchParams);
}

async function fetchQcData(params: {
  date: string;
  siteId: string;
  location?: string | null;
}): Promise<QcDataResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<QcDataResponse>('qc', searchParams);
}

async function fetchShowStopSign(siteId: string): Promise<boolean> {
  return fetchGet<boolean>(`stop-sign/${siteId}`);
}

async function refreshStopGoSignFn(params: {
  siteId: string;
  val: string;
}): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('val', params.val);
  return fetchMutation<boolean>(
    `stop-sign/${params.siteId}`,
    'PUT',
    undefined,
    searchParams,
  );
}

async function updateAspirationDataEntryFn(params: {
  input: EmbryologistDataEntryRequest;
  isReturn: string;
}): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('isReturn', params.isReturn);
  return fetchMutation<boolean>(
    'aspirations/data-entry',
    'PUT',
    params.input,
    searchParams,
  );
}

async function createAspPoolFn(request: CreateAspPoolRequest): Promise<string> {
  return fetchMutation<string>('pools', 'POST', request);
}

async function removeAspPoolFn(
  request: RemoveAspPoolRequest,
): Promise<boolean> {
  return fetchMutation<boolean>('pools', 'DELETE', request);
}

async function updateQcDataFn(params: {
  input: QcDataUpdateRequest;
  aspirationDate: string;
  siteId: string;
  location?: string | null;
}): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  if (params.location) searchParams.set('location', params.location);
  return fetchMutation<boolean>('qc', 'PUT', params.input, searchParams);
}

export const completedAspirationsOptions = (params: {
  date: string;
  siteId: string;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'aspirations-completed', params],
    async queryFn() {
      return fetchCompletedAspirations(params);
    },
  });

export const completedAspirationsFbdOptions = (params: {
  date: string;
  siteId: string;
  location: string;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'aspirations-completed-fbd', params],
    async queryFn() {
      return fetchCompletedAspirationsFbd(params);
    },
  });

export const fertDataOptions = (params: {
  donorId: string;
  ranch?: string | null;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'fert-data', params],
    async queryFn() {
      return fetchFertData(params);
    },
  });

export const statsOptions = (params: {
  aspirationDate: string;
  siteId: string;
  location?: string | null;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'stats', params],
    async queryFn() {
      return fetchStats(params);
    },
  });

export const statsPseOptions = (params: {
  aspirationDate: string;
  siteId: string;
  location?: string | null;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'stats-pse', params],
    async queryFn() {
      return fetchStatsPse(params);
    },
  });

export const entriesOptions = (params: {
  aspirationDate: string;
  siteId: string;
  techId: string;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'entries', params],
    async queryFn() {
      return fetchEntries(params);
    },
  });

export const entriesPseOptions = (params: {
  aspirationDate: string;
  siteId: string;
  pseId: string;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'entries-pse', params],
    async queryFn() {
      return fetchEntriesPse(params);
    },
  });

export const techAveragesOptions = (params: {
  aspirationDate: string;
  siteId: string;
  techId: string;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'tech-averages', params],
    async queryFn() {
      return fetchTechAverages(params);
    },
  });

export const averagesOptions = (params: {
  aspirationDate: string;
  siteId: string;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'averages', params],
    async queryFn() {
      return fetchAverages(params);
    },
  });

export const poolOptions = (params: {
  aspirationDate: string;
  siteId: string;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'pool', params],
    async queryFn() {
      return fetchPool(params);
    },
  });

export const qcDataOptions = (params: {
  date: string;
  siteId: string;
  location?: string | null;
}) =>
  queryOptions({
    queryKey: ['opu-search', 'qc', params],
    async queryFn() {
      return fetchQcData(params);
    },
  });

export const showStopSignOptions = (siteId: string) =>
  queryOptions({
    queryKey: ['opu-search', 'stop-sign', siteId],
    async queryFn() {
      return fetchShowStopSign(siteId);
    },
  });

export function useRefreshStopGoSignMutation() {
  return useMutation({
    mutationFn: (params: { siteId: string; val: string }) =>
      refreshStopGoSignFn(params),
  });
}

export function useUpdateAspirationDataEntryMutation() {
  return useMutation({
    mutationFn: (params: {
      input: EmbryologistDataEntryRequest;
      isReturn: string;
    }) => updateAspirationDataEntryFn(params),
  });
}

export function useCreateAspPoolMutation() {
  return useMutation({
    mutationFn: (request: CreateAspPoolRequest) => createAspPoolFn(request),
  });
}

export function useRemoveAspPoolMutation() {
  return useMutation({
    mutationFn: (request: RemoveAspPoolRequest) => removeAspPoolFn(request),
  });
}

export function useUpdateQcDataMutation() {
  return useMutation({
    mutationFn: (params: {
      input: QcDataUpdateRequest;
      aspirationDate: string;
      siteId: string;
      location?: string | null;
    }) => updateQcDataFn(params),
  });
}
